import { Alert, AlertDescription, AlertTitle } from "@components/ui/alert";
import { UserSubscription } from "./types";
import useDialog, { DialogProvider } from "@hooks/useDialog";
import { Button } from "@components/ui/button";
import { useAuth } from "@hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import { authClient } from "@utils/authClient";
import { supabase } from "@utils/serverClient";
import { DateTime } from "luxon";
import { Organization } from "types/index";
import { serverEndpoints } from "@utils/serverEndpoints";
import SignageBillingProvider from "@components/shared/Billing/SignageBillingProvider";
import {
  AddNewPaymentMethod,
  PaymentMethodsProvider,
  AdminProvider,
  DialogProvider as BillingDialogProvider,
} from "@growthzilla/billingui";
import { Cross2Icon } from "@radix-ui/react-icons";
import { CircularProgressIndicator } from "@components/shared/CircularProgressIndicator";

import React, { useState } from "react";
import { MdOutlineWarningAmber } from "react-icons/md";

export function DraggableAlertUI({
  alertInfo,
}: {
  alertInfo: {
    title: string;
    description: string;
    trailing?: React.ReactNode;
  };
}) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [rel, setRel] = useState<{
    x: number;
    y: number;
  } | null>(null);

  function onMouseDown(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (e.button !== 0) return;
    setDragging(true);
    setRel({
      x: e.pageX - position.x,
      y: window.innerHeight - e.pageY - position.y,
    });
    e.stopPropagation();
    e.preventDefault();
  }

  function onMouseUp(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    setDragging(false);
    e.stopPropagation();
    e.preventDefault();
  }

  function onMouseMove(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!dragging || !rel) return;

    const newX = e.pageX - rel.x;
    const newY = window.innerHeight - e.pageY - rel.y;

    // Prevent the draggable div from going off the left or right edges
    const maxX = window.innerWidth - 576;
    const adjustedX = Math.min(Math.max(0, newX), maxX);

    // Prevent the draggable div from going off the top or bottom edges
    const maxY = window.innerHeight - 150;
    const adjustedY = Math.min(Math.max(0, newY), maxY);

    setPosition({
      x: adjustedX,
      y: adjustedY,
    });

    e.stopPropagation();
    e.preventDefault();
  }

  return (
    <div
      className="fixed bottom-0 left-0 flex flex-col gap-3 p-3"
      style={{
        left: `${position.x}px`,
        bottom: `${position.y}px`,
        position: "fixed",
        width: "576px",
      }}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseMove={onMouseMove}
    >
      <DialogProvider>
        <AlertUI
          {...alertInfo}
          icon={<MdOutlineWarningAmber className="text-xl" />}
        />
      </DialogProvider>
    </div>
  );
}

function AlertUI({
  icon,
  title,
  description,
  trailing,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <Alert className="bg-background shadow-md">
      {icon}
      <AlertTitle>{title}</AlertTitle>

      <div className="flex items-center gap-2">
        <div className="w-full flex-1">
          {description && <AlertDescription>{description}</AlertDescription>}
        </div>

        {trailing}
      </div>
    </Alert>
  );
}

export function AddPaymentMethodButton({
  isOnFreeTrial = false,
  subscription,
}: {
  isOnFreeTrial?: boolean;
  subscription?: UserSubscription;
}) {
  const { openDialog, closeDialog } = useDialog();
  return (
    <Button
      variant={"ghost"}
      size={"sm"}
      onClick={() => {
        openDialog({
          title: "Add Payment Method",
          maxWidth: "60vw",
          height: "70vh",
          showActions: false,
          dialogType: "loading",
          hideCloseButton: true,
          customContent: (
            <AddPaymentMethodDialog
              closeDialog={closeDialog}
              isOnFreeTrial={isOnFreeTrial}
              subscription={subscription}
            />
          ),
        });
      }}
    >
      Add Payment Method
    </Button>
  );
}

export function AddPaymentMethodDialog({
  isOnFreeTrial,
  closeDialog,
  subscription,
}: {
  isOnFreeTrial: boolean;
  closeDialog: () => void;
  subscription?: UserSubscription;
}) {
  const { user } = useAuth();

  const { mutate: assignPaymentIdToSub, status } = useMutation({
    mutationFn: async ({
      sub,
      paymentMethodId,
    }: {
      sub?: UserSubscription;
      paymentMethodId: number;
    }) => {
      if (!sub) return;
      if (!user) return;

      const accessToken = await authClient.getAccessToken();
      const org_id = user?.org_id;

      if (isOnFreeTrial) {
        const addedTwoMonths = DateTime.fromISO(sub.billing_start_date).plus({
          month: 2,
        });

        const billing_start_date = addedTwoMonths.toFormat(`yyyy-MM-dd`);

        const promoName: Organization["plan_status"] = "extended_free_trial";

        const data = await fetch(serverEndpoints.extendFreeTrial, {
          method: "POST",
          body: JSON.stringify({
            subscriptionId: sub.id,
            paymentMethodId: paymentMethodId,
            billing_start_date: billing_start_date,
            promoName: promoName,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "user-id": user?.id ?? "",
            "business-id": org_id ?? "",
          },
        });
        if (org_id) {
          const { error } = await supabase
            .from("organization")
            .update({ plan_status: "extended_free_trial" })
            .eq("id", org_id);

          if (error) {
            return;
          }
        }

        const _json = await data.json();

        return _json;
      } else {
        const data = await fetch(serverEndpoints.addPaymentToSub, {
          method: "POST",
          body: JSON.stringify({
            payment_method_id: paymentMethodId,
            subscription_id: sub.id,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "user-id": user?.id ?? "",
            "business-id": org_id ?? "",
          },
        });

        const _json = await data.json();

        return _json;
      }
    },
    onSuccess() {
      window.location.reload();
    },
  });

  return (
    <div className="z-0 h-full w-full pt-4">
      <SignageBillingProvider>
        <BillingDialogProvider>
          <AdminProvider isAdmin={false}>
            <PaymentMethodsProvider>
              <AddNewPaymentMethod
                showDialogOnAdd={false}
                hideTitle={true}
                onAdded={(method) => {
                  assignPaymentIdToSub({
                    paymentMethodId: method.id,
                    sub: subscription,
                  });

                  closeDialog();
                }}
              />
            </PaymentMethodsProvider>
          </AdminProvider>
        </BillingDialogProvider>
      </SignageBillingProvider>

      <button
        type="button"
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        onClick={() => {
          closeDialog();
        }}
      >
        <Cross2Icon className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </button>

      {status === "pending" && (
        <div className="absolute inset-0 m-auto flex flex-col items-center justify-center bg-black/20">
          <CircularProgressIndicator />
        </div>
      )}
    </div>
  );
}
